import { EntityType } from '@frontend/common';
import { DefaultBadge } from '@frontend/components/badge';
import { accountIdFromResolvedEntity } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { CommonMessage } from '@frontend/lang';
import { ModalType } from '@frontend/modal';
import { RequirementExtendedClient } from '@frontend/stock/api';
import { RequirementExtended } from '@frontend/stock/types';
import { FormattedMessage } from 'react-intl';

import { MultiEntityButton } from '../../components/multi-entity-button/multi-entity-button.component';
import { PlatformObjectBadge } from '../../modal/platform-object-badge/platform-object-badge.component';
import OverviewHeader from '../header/overview-header';
import { CreateRequirementModal } from '../modals/create-requirement-modal/create-requirement-modal.component';
import { useRequirements } from './requirements.controller';

export const Requirements = () => {
    const viewProps = useRequirements();

    return (
        <div className='size-lf-stretch bg-background-default inline-flex w-full flex-col items-start justify-start gap-6'>
            <OverviewHeader
                title={
                    <div className='flex gap-2'>
                        <span>{CommonMessage.OBJECTS.REQUIREMENT.PLURAL}</span>
                        <span>/</span>
                        <span>
                            <FormattedMessage
                                id='requirements.title'
                                defaultMessage='Stock requirements'
                            />
                        </span>
                    </div>
                }
                create={{
                    onCreate: () =>
                        viewProps.openModal(
                            viewProps.REQUIREMENT_MODAL_ID,
                            () => <CreateRequirementModal />,
                            {},
                            { type: ModalType.PANE, className: 'max-w-[900px]' }
                        ),
                    entity: EntityType.STOCK_REQUIREMENT,
                    overwriteCreateLabel: (
                        <div className='flex gap-1'>
                            <span>{CommonMessage.BUTTONS.CREATE}</span>
                            <span className='lowercase'>
                                <FormattedMessage
                                    id='requirements.stock-requirement'
                                    defaultMessage='Stock requirement'
                                />
                            </span>
                        </div>
                    )
                }}
            />
            <ReactiveObjectList<RequirementExtended>
                key='requirements-list'
                fetch={(arg) => RequirementExtendedClient.fetchExtendedRequirements(arg)}
                dispatch={viewProps.dispatch}
                eventListener={EntityType.REQUIREMENT}
                type={EntityType.PRODUCT}
                columns={{
                    columns: ['spot', 'product', 'categories', 'sku', 'ean', 'min', 'max'],
                    customCells: new Map([
                        [
                            'spot',
                            (obj) => (
                                <td className={defaultCellClassName}>
                                    <div className='w-[250px]'>
                                        {obj.row.original.spot ? (
                                            <PlatformObjectBadge
                                                entityType={EntityType.SPOT}
                                                id={obj.row.original.spot.entity_id}
                                                display={obj.row.original.spot.name}
                                                key={obj.row.original.spot.entity_id}
                                                foreign={obj.row.original.spot.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.spot)}
                                            />
                                        ) : (
                                            <span className='text-foreground-muted'>/</span>
                                        )}
                                    </div>
                                </td>
                            )
                        ],
                        [
                            'product',
                            (obj) => (
                                <td className={defaultCellClassName}>
                                    <div className='w-[300px]'>
                                        {obj.row.original.product ? (
                                            <PlatformObjectBadge
                                                entityType={EntityType.PRODUCT}
                                                id={obj.row.original.product.entity_id}
                                                display={obj.row.original.product.name}
                                                key={obj.row.original.product.entity_id}
                                                foreign={obj.row.original.product.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.product)}
                                            />
                                        ) : (
                                            <span className='text-foreground-muted'>/</span>
                                        )}
                                    </div>
                                </td>
                            )
                        ],
                        [
                            'categories',
                            (obj) => {
                                if (!obj.row.original.categories || obj.row.original.categories.length === 0) {
                                    return (
                                        <td className={defaultCellClassName}>
                                            <div className='w-[250px]'>
                                                <span className='text-foreground-muted'>/</span>
                                            </div>
                                        </td>
                                    );
                                }

                                return (
                                    <td
                                        key={obj.row.original.categories[0].entity_id}
                                        className={defaultCellClassName}>
                                        <div className='flex w-[250px] gap-2.5 truncate text-sm font-medium'>
                                            <PlatformObjectBadge
                                                id={obj.row.original.categories[0].entity_id}
                                                entityType={EntityType.PRODUCT_CATEGORY}
                                                display={obj.row.original.categories[0].name}
                                                key={obj.row.original.categories[0].entity_id}
                                                foreign={obj.row.original.categories[0].foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.categories[0])}
                                            />
                                            {obj.row.original.categories.length > 1 && (
                                                <MultiEntityButton
                                                    entities={obj.row.original.categories.slice(1)}
                                                    render={(category) => (
                                                        <PlatformObjectBadge
                                                            id={category.entity_id}
                                                            entityType={EntityType.PRODUCT_CATEGORY}
                                                            display={category.name}
                                                            key={category.entity_id}
                                                            foreign={category.foreign}
                                                            accountId={accountIdFromResolvedEntity(category)}
                                                        />
                                                    )}
                                                />
                                            )}
                                        </div>
                                    </td>
                                );
                            }
                        ],
                        [
                            'sku',
                            (obj) => (
                                <td className={defaultCellClassName}>
                                    <div className='w-[150px]'>
                                        {obj.row.original.sku ? (
                                            <span className='text-foreground-default'>{obj.row.original.sku}</span>
                                        ) : (
                                            <span className='text-foreground-muted'>/</span>
                                        )}
                                    </div>
                                </td>
                            )
                        ],
                        [
                            'ean',
                            (obj) => (
                                <td className={defaultCellClassName}>
                                    <div className='w-[150px]'>
                                        {obj.row.original.ean ? (
                                            <span className='text-foreground-default'>{obj.row.original.ean}</span>
                                        ) : (
                                            <span className='text-foreground-muted'>/</span>
                                        )}
                                    </div>
                                </td>
                            )
                        ],
                        [
                            'min',
                            (obj) => (
                                <td className={defaultCellClassName}>
                                    <div className='w-[150px]'>
                                        <DefaultBadge
                                            label={obj.row.original.min.toString()}
                                            styles={{
                                                container: {
                                                    color: 'bg-background-surface'
                                                },
                                                label: 'text-foreground-default'
                                            }}
                                        />
                                    </div>
                                </td>
                            )
                        ],
                        [
                            'max',
                            (obj) => (
                                <td className={defaultCellClassName}>
                                    <div className='w-[150px]'>
                                        <DefaultBadge
                                            label={obj.row.original.max.toString()}
                                            styles={{
                                                container: {
                                                    color: 'bg-background-surface'
                                                },
                                                label: 'text-foreground-default'
                                            }}
                                        />
                                    </div>
                                </td>
                            )
                        ]
                    ]),
                    headerOverwrite: new Map([
                        ['spot', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                        ['product', CommonMessage.OBJECTS.PRODUCT.DEFAULT],
                        ['categories', CommonMessage.OBJECTS.CATEGORY.DEFAULT],
                        ['sku', CommonMessage.OBJECTS.PRODUCT.SKU],
                        ['ean', CommonMessage.OBJECTS.PRODUCT.EAN],
                        ['min', CommonMessage.OBJECTS.STOCK.MIN_STOCK],
                        ['max', CommonMessage.OBJECTS.STOCK.MAX_STOCK]
                    ])
                }}
                activeFilter={{
                    searchKeys: ['product'],
                    filterKeys: ['spot_id'],
                    displayName: new Map([['product', CommonMessage.OBJECTS.PRODUCT.DEFAULT]])
                }}
            />
        </div>
    );
};
